/**
 * Weather Service - OpenWeatherMap API integration
 * Requires VITE_OPENWEATHERMAP_API_KEY environment variable
 */

const API_KEY = import.meta.env.VITE_OPENWEATHERMAP_API_KEY || '';
const BASE_URL = 'https://api.openweathermap.org/data/2.5';

export interface WeatherData {
    temp: number;
    tempMin: number;
    tempMax: number;
    feelsLike: number;
    description: string;
    icon: string;
    humidity: number;
    windSpeed: number;
    city: string;
}

// Map OpenWeatherMap icon codes to emoji
const iconToEmoji: Record<string, string> = {
    '01d': '☀️', // clear sky day
    '01n': '🌙', // clear sky night
    '02d': '⛅', // few clouds day
    '02n': '☁️', // few clouds night
    '03d': '☁️', // scattered clouds
    '03n': '☁️',
    '04d': '☁️', // broken clouds
    '04n': '☁️',
    '09d': '🌧️', // shower rain
    '09n': '🌧️',
    '10d': '🌦️', // rain day
    '10n': '🌧️', // rain night
    '11d': '⛈️', // thunderstorm
    '11n': '⛈️',
    '13d': '❄️', // snow
    '13n': '❄️',
    '50d': '🌫️', // mist
    '50n': '🌫️',
};

export function getWeatherEmoji(iconCode: string): string {
    return iconToEmoji[iconCode] || '🌡️';
}

/**
 * Get current weather by coordinates
 */
export async function getWeatherByCoords(lat: number, lon: number): Promise<WeatherData | null> {
    if (!API_KEY) {
        console.warn('[Weather] No API key configured');
        return null;
    }

    try {
        const response = await fetch(
            `${BASE_URL}/weather?lat=${lat}&lon=${lon}&units=imperial&appid=${API_KEY}`
        );

        if (!response.ok) {
            throw new Error('Weather API request failed');
        }

        const data = await response.json();

        return {
            temp: Math.round(data.main.temp),
            tempMin: Math.round(data.main.temp_min),
            tempMax: Math.round(data.main.temp_max),
            feelsLike: Math.round(data.main.feels_like),
            description: data.weather[0].description,
            icon: data.weather[0].icon,
            humidity: data.main.humidity,
            windSpeed: Math.round(data.wind.speed),
            city: data.name,
        };
    } catch (error) {
        console.error('[Weather] Failed to fetch weather:', error);
        return null;
    }
}

/**
 * Get current weather by city name
 */
export async function getWeatherByCity(city: string): Promise<WeatherData | null> {
    if (!API_KEY) {
        console.warn('[Weather] No API key configured');
        return null;
    }

    try {
        const response = await fetch(
            `${BASE_URL}/weather?q=${encodeURIComponent(city)}&units=imperial&appid=${API_KEY}`
        );

        if (!response.ok) {
            throw new Error('Weather API request failed');
        }

        const data = await response.json();

        return {
            temp: Math.round(data.main.temp),
            tempMin: Math.round(data.main.temp_min),
            tempMax: Math.round(data.main.temp_max),
            feelsLike: Math.round(data.main.feels_like),
            description: data.weather[0].description,
            icon: data.weather[0].icon,
            humidity: data.main.humidity,
            windSpeed: Math.round(data.wind.speed),
            city: data.name,
        };
    } catch (error) {
        console.error('[Weather] Failed to fetch weather:', error);
        return null;
    }
}

/**
 * Get user's current location weather
 * Falls back to default city if geolocation fails or is denied
 */
export async function getCurrentLocationWeather(): Promise<WeatherData | null> {
    const DEFAULT_CITY = 'New York';

    // If no API key, return null immediately
    if (!API_KEY) {
        console.warn('[Weather] No API key configured - check VITE_OPENWEATHERMAP_API_KEY in .env');
        return null;
    }

    console.log('[Weather] Fetching weather data...');

    return new Promise((resolve) => {
        // First, check if geolocation is available
        if (!navigator.geolocation) {
            console.log('[Weather] Geolocation not supported, using default city');
            getWeatherByCity(DEFAULT_CITY).then(resolve);
            return;
        }

        // Set a timeout - if geolocation takes too long, use default city
        const timeoutId = setTimeout(() => {
            console.log('[Weather] Geolocation timed out, using default city');
            getWeatherByCity(DEFAULT_CITY).then(resolve);
        }, 8000);

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                clearTimeout(timeoutId);
                console.log('[Weather] Got geolocation:', position.coords.latitude, position.coords.longitude);
                const weather = await getWeatherByCoords(
                    position.coords.latitude,
                    position.coords.longitude
                );
                if (weather) {
                    console.log('[Weather] Weather fetched successfully:', weather.city);
                    resolve(weather);
                } else {
                    // API call failed, try fallback
                    console.log('[Weather] Coords fetch failed, trying default city');
                    getWeatherByCity(DEFAULT_CITY).then(resolve);
                }
            },
            async (error) => {
                clearTimeout(timeoutId);
                console.log('[Weather] Geolocation denied or failed:', error.message);
                // Fall back to default city
                const weather = await getWeatherByCity(DEFAULT_CITY);
                resolve(weather);
            },
            {
                timeout: 5000,
                enableHighAccuracy: false,
                maximumAge: 300000 // 5 minutes cache
            }
        );
    });
}
